import { CardFormTemplate } from '@coast/schemas';
import { isNil } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import { MBButton } from '@shared/components/MBButton';
import { UnvalidatedStyles } from '@shared/components/MBText';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBCardBlocks } from '@shared/components/blocks';
import { CardChannelContext } from '@shared/components/blocks/contexts/channel';
import { MBCardFormProvider } from '@shared/components/blocks/contexts/form';
import { CardRenderContext } from '@shared/components/blocks/contexts/render';
import { CardBlockScrollContext } from '@shared/components/blocks/contexts/scroll';
import { MBCardTemplateProvider } from '@shared/components/blocks/contexts/template';
import { BlockViewTemplateProvider } from '@shared/components/blocks/contexts/viewTemplate';
import { CommonFields, Fields } from '@shared/components/blocks/types';
import { correctMissingBlockData } from '@shared/components/blocks/utils/correctMissingBlockData';
import { MBEmptyListPlaceholder } from '@shared/components/list';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTextH4 } from '@shared/components/typography';
import { ExternalBatchContextProvider } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { FormErrorBanner } from '@shared/scenes/cards/form/components/errorBanner';
import { useCreateCardMutation } from '@shared/scenes/cards/form/hooks';
import { useCardDefinition, useStickyBlocks } from '@shared/scenes/cards/hooks';
import { CardTemplate } from '@shared/scenes/cards/types';
import {
  useViewTemplateWithUITemplate,
  useWorkflowTemplateWithCardDefinition,
} from '@shared/scenes/workflows/hooks';
import { Colors } from '@shared/styles';
import { API } from '@shared/types/api';
import { useChannel, useFunctionState } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';

interface Props {
  channelId: number;
  templateSlug?: string | null;
}

export function ExternalCardForm({ channelId, templateSlug }: Props) {
  const channel = useChannel({ channelId });
  const { cardDefinition } = useCardDefinition(channel?.id, channel?.workflowTemplateId);
  const { workflowTemplate } = useWorkflowTemplateWithCardDefinition(channel?.workflowTemplateId);

  const cardDefinitionId = cardDefinition?.id ?? undefined;
  const workflowTemplateId = channel?.workflowTemplateId ?? undefined;

  const createCard = useCreateCardMutation(channelId);
  const submit = useCallback(
    async (fields: CommonFields & Fields) => {
      const card = await createCard(cardDefinitionId, workflowTemplateId, fields);
      const requestorFullName = card?.fields?.requestorFullName ?? '';
      const cardName = card?.fields?.name ?? '';
      const cardDefinitionName = cardDefinition?.name ?? '';
      const REDIRECT_URL = import.meta.env.VITE_FORM_SUBMITTED_REDIRECT_URL;
      window.location.href = `${REDIRECT_URL}?fullName=${requestorFullName}&cardName=${cardName}&cardDefinitionName=${cardDefinitionName}`;
    },
    [createCard, cardDefinitionId, workflowTemplateId, cardDefinition?.name],
  );

  const [onSubmit, setOnSubmit] = useFunctionState();

  const { viewTemplate, loading } = useViewTemplateWithUITemplate(
    channel?.workflowTemplateId,
    templateSlug,
  );

  const template = !isNil(viewTemplate)
    ? (viewTemplate.cardUITemplate.template as CardFormTemplate)
    : cardDefinition?.templates
        ?.map(t => t.template as CardTemplate)
        .find(
          (t): t is CardFormTemplate =>
            (!isNil(templateSlug) && isMatchingNoCodeExternalFormUITemplate(t, templateSlug)) ||
            (isNil(templateSlug) && isLowCodeExternalFormUITemplate(t)),
        );
  const blocks = useMemo(
    () => correctMissingBlockData(template?.cardBlocks, workflowTemplate?.components),
    [template, workflowTemplate],
  );

  const scrollView = useContext(CardBlockScrollContext);
  const { top, scroll, bottom } = useStickyBlocks(blocks);

  if (!channel || !cardDefinition || loading) {
    return <MBLoadingIndicator fill center padding={4} />;
  }
  if (!template) {
    return <MBEmptyListPlaceholder title={i18n('externalForm.noTemplateFound')} />;
  }

  return (
    <CardChannelContext.Provider value={channel}>
      <ExternalBatchContextProvider>
        <CardRenderContext.Provider value="form">
          <MBCardTemplateProvider template={template}>
            <BlockViewTemplateProvider template={viewTemplate ?? null}>
              <MBCardFormProvider
                card={{ cardDefinitionId: cardDefinition?.id } as API.CardFragment}
                blocks={blocks}
                cardDefinitionId={cardDefinition?.id}
                fieldOverrides={template.fieldOverrides}
                onSubmit={submit}
                onSubmitFormButtonCallback={setOnSubmit}
              >
                <View fill>
                  <MBView fill style={styles.fieldContainer}>
                    <View
                      row
                      centerVertically
                      style={{
                        height: 64,
                        justifyContent: 'space-between',
                        borderBottomColor: Colors.v2.gray05,
                        borderBottomWidth: 1,
                      }}
                      padding={1}
                    >
                      <View flex paddingHorizontal={2}>
                        <MBTextH4
                          align="left"
                          ellipsizeMode="tail"
                          numberOfLines={1}
                          style={UnvalidatedStyles.numberOfLines(1)}
                        >
                          {template.title}
                        </MBTextH4>
                      </View>
                      <MBButton
                        style={{ width: 100 }}
                        title={i18n('common.create')}
                        onPress={onSubmit}
                        size={'small'}
                      />
                    </View>
                    <ScrollView>
                      <FormErrorBanner />
                      <MBCardBlocks
                        blocks={top}
                        card={{ cardDefinitionId: cardDefinition?.id } as API.CardFragment}
                        sticky
                      />
                      <ScrollView
                        ref={scrollView}
                        keyboardShouldPersistTaps="always"
                        keyboardDismissMode="on-drag"
                      >
                        <MBCardBlocks
                          blocks={scroll}
                          card={{ cardDefinitionId: cardDefinition?.id } as API.CardFragment}
                        />
                      </ScrollView>
                      <MBCardBlocks
                        blocks={bottom}
                        card={{ cardDefinitionId: cardDefinition?.id } as API.CardFragment}
                        sticky
                      />
                    </ScrollView>
                  </MBView>
                </View>
              </MBCardFormProvider>
            </BlockViewTemplateProvider>
          </MBCardTemplateProvider>
        </CardRenderContext.Provider>
      </ExternalBatchContextProvider>
    </CardChannelContext.Provider>
  );
}

function isMatchingNoCodeExternalFormUITemplate(template: CardTemplate, slug: string): boolean {
  return isNoCodeCardFormTemplate(template) && slug === template.slug;
}

function isNoCodeCardFormTemplate(template: CardTemplate): template is CardFormTemplate {
  return ['nocode-public-create', 'nocode-public-readonly'].includes(template.context);
}

function isLowCodeExternalFormUITemplate(template: CardTemplate): boolean {
  return template.context === 'create-external';
}

const styles = StyleSheet.create({
  fieldContainer: {
    width: '100%',
    maxWidth: 456, // copied from Figma
    alignSelf: 'center',
  },
});
